import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { analytics } from '../lib/analytics';
import {
  useSEO,
  useStructuredData,
  generateFAQStructuredData,
  COMPETITIVE_KEYWORD_CLUSTERS,
  mergeKeywordSets,
} from '../lib/seo';
import { 
  ArrowLeft, 
  Search,
  HelpCircle,
  Lock,
  CreditCard,
  BarChart3,
  Users,
  Shield,
  Download,
  Smartphone
} from 'lucide-react';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  icon: any;
}

const faqs: FAQItem[] = [
  // Tension Score & How It Works
  {
    id: 'what-is-rti',
    category: 'Tension Score & How It Works',
    icon: BarChart3,
    question: 'What is the tension score (RTI)?',
    answer: 'The tension score (RTI) is a 0-100 score that measures relationship pressure across 5 behavior signals: who starts contact, emotional state, reply speed, communication quality, and withdrawal signs. Each signal is scored 0-100, then averaged into one simple score. Under 40 is usually stable, 40-60 is manageable tension, and 60+ is high tension that needs faster action.'
  },
  {
    id: 'how-accurate',
    category: 'Tension Score & How It Works',
    icon: BarChart3,
    question: 'How accurate is the tension score?',
    answer: 'The tension score is based on your own check-ins, so accuracy improves with honest and consistent entries. Patterns usually become clearer within 7-10 days, and forecast quality improves after about 30 days of data.'
  },
  {
    id: 'multiple-relationships',
    category: 'Tension Score & How It Works',
    icon: Users,
    question: 'Can I track multiple relationships?',
    answer: 'Yes. TENSION supports unlimited relationships on all plans. Go to Settings → Manage Relationships to add, switch, or archive relationships. Each one keeps its own scores, timeline, and insights.'
  },
  {
    id: 'what-is-forecasting',
    category: 'Tension Score & How It Works',
    icon: BarChart3,
    question: 'How does forecasting work?',
    answer: 'Forecasting looks at your recent check-ins to estimate where your tension score is heading in the next 7, 14, and 30 days. It works best after at least 7 consistent check-ins and gets better with more data.'
  },
  {
    id: 'partner-find-app',
    category: 'Tension Score & How It Works',
    icon: Lock,
    question: 'What if my partner finds this app on my phone?',
    answer: 'TENSION is designed to be discreet. You can: 1) Use a generic partner identifier instead of their name (e.g., "Partner A"), 2) Enable the privacy mode in Settings to hide sensitive data on the home screen, 3) Install as a PWA with a neutral icon, or 4) Explain it as a personal emotional tracking tool (which it is). Remember: tracking your own feelings and behaviors is healthy self-awareness, not surveillance.'
  },

  // Privacy & Data
  {
    id: 'data-storage',
    category: 'Privacy & Data',
    icon: Lock,
    question: 'Where is my data stored?',
    answer: 'Core relationship data (check-ins, journal-style entries, partner labels you enter) is stored locally in your browser by default. It does not leave your device unless you export it or turn on optional cloud sync (paid plans). Your account (email, sign-in session, subscription status) lives on Supabase with TLS in transit and industry-standard protections—we do not describe that as “end-to-end encrypted” in the chat-app sense; see the Privacy Policy for exactly what that means. Bottom line: sensitive tracking data is local-first unless you choose sync.'
  },
  {
    id: 'data-sharing',
    category: 'Privacy & Data',
    icon: Shield,
    question: 'Do you share or sell my data?',
    answer: 'We do not sell your personal data. We may use aggregated or operational analytics to run and improve the product; we aim to minimize personally identifiable collection outside what is needed for your account and security. Practices are described in our Privacy Policy, and we support access, deletion, and export requests where applicable. If you are in the EU or California, additional rights may apply—contact us to exercise them.'
  },
  {
    id: 'export-data',
    category: 'Privacy & Data',
    icon: Download,
    question: 'Can I export my data?',
    answer: 'Yes! Go to Settings → Data & Backup → Export Data to download a complete JSON backup of all your TENSION data (check-ins, relationships, settings, purchased packs). You can also generate a PDF report or therapist-friendly export. Exported data can be imported on another device or kept as a permanent backup. We recommend exporting monthly as a safety precaution.'
  },
  {
    id: 'delete-account',
    category: 'Privacy & Data',
    icon: Shield,
    question: 'How do I delete my account and data?',
    answer: 'Go to Settings → Account → Delete Account. You\'ll be asked to confirm via email and acknowledge that deletion is permanent. This removes your account from our servers and clears all local data. We recommend exporting your data first if you want to keep a record. Deletion takes effect immediately and cannot be undone. Refunds may be available depending on your subscription status.'
  },
  {
    id: 'multi-device-sync',
    category: 'Privacy & Data',
    icon: Smartphone,
    question: 'Can I sync my data across multiple devices?',
    answer: 'Yes! Cloud sync is available for all paid plans. Your check-ins, relationships, and settings automatically sync across all devices where you\'re signed in. Data is encrypted in transit and at rest. To enable: Sign in with the same account on all devices, then go to Settings → Sync & Backup → Enable Cloud Sync. Free plan users can manually export/import data between devices.'
  },

  // Pricing & Subscriptions
  {
    id: 'pricing-tiers',
    category: 'Pricing & Subscriptions',
    icon: CreditCard,
    question: 'What are the differences between Free, Premium, and Pro?',
    answer: 'Free: Unlimited check-ins, basic tension scoring, 30-day timeline, and basic action cards. Premium: Advanced insights (forecasting, pattern analysis, attachment journey), stronger action cards, unlimited timeline, PDF export, and priority support. Pro: Everything in Premium plus therapist export, communication templates, anxiety-vs-reality tracking, rumination detection, and custom coaching programs. All plans include unlimited relationships and offline access.'
  },
  {
    id: 'situational-packs',
    category: 'Pricing & Subscriptions',
    icon: CreditCard,
    question: 'What are Situational Packs?',
    answer: 'Situational Packs are one-time purchases ($9.99-$19.99) for specific scenarios: Breakup/No Contact ($19.99 - 21 days), Rebuild After Cheating ($19.99 - 30 days), Long Distance ($14.99 - ongoing), New Relationship ($9.99 - 90 days), etc. Each pack includes daily check-ins, specialized action cards, and contextual prompts tailored to your situation. Packs are permanent purchases (no subscription) and stack with your base plan.'
  },
  {
    id: 'cancel-subscription',
    category: 'Pricing & Subscriptions',
    icon: CreditCard,
    question: 'How do I cancel my subscription?',
    answer: 'Subscriptions are managed through Stripe. To cancel: 1) Go to Settings → Subscription, 2) Click "Manage Subscription" to open the Stripe portal, 3) Click "Cancel Plan". Your access continues until the end of your billing period, then you\'ll revert to the Free plan. No data is lost when downgrading. Refunds are available within 30 days of initial purchase (contact support@tension.app).'
  },
  {
    id: 'refund-policy',
    category: 'Pricing & Subscriptions',
    icon: CreditCard,
    question: 'What is your refund policy?',
    answer: 'We offer full refunds within 30 days of your first subscription payment, no questions asked. For Situational Packs, refunds are available within 7 days if you haven\'t completed more than 3 days of the program. To request a refund, email support@tension.app with your account email and order details. Refunds typically process within 5-7 business days.'
  },

  // Features & Usage
  {
    id: 'action-cards',
    category: 'Features & Usage',
    icon: HelpCircle,
    question: 'What are Action Cards and how do I use them?',
    answer: 'Action Cards are personalized next steps based on your current tension score. In Stable, you get maintenance actions. In Elastic, you get prevention actions. In High Tension, you get de-escalation actions. Cards include scripts, timing guidance, and expected outcomes. Mark them complete to learn what works best for you.'
  },
  {
    id: 'check-in-frequency',
    category: 'Features & Usage',
    icon: HelpCircle,
    question: 'How often should I check in?',
    answer: 'Daily check-ins are best for the first 30 days to build a baseline. After that, 3-5 times per week usually works well. Check in more often during high-tension periods or major relationship events. Consistent timing gives the most reliable score trends.'
  },
  {
    id: 'break-mode',
    category: 'Features & Usage',
    icon: HelpCircle,
    question: 'What is Break Mode?',
    answer: 'Break Mode pauses tracking without deleting your data. Use it during relationship breaks, travel, or when you need space from the app. Your historical data is preserved, and you can resume anytime. To activate: Settings → Break Mode → Set Reason (optional) → Activate. When you return, your timeline and insights will pick up where you left off. You can also archive a relationship permanently if it has ended.'
  },
  {
    id: 'offline-mode',
    category: 'Features & Usage',
    icon: Smartphone,
    question: 'Does TENSION work offline?',
    answer: 'Yes! TENSION is a Progressive Web App (PWA) with full offline functionality. You can complete check-ins, view your timeline, and access action cards without internet. Data syncs automatically when you reconnect. To install: Settings → Install as App, or tap "Add to Home Screen" when prompted. Offline mode is perfect for flights, remote areas, or data-saving scenarios.'
  },

  // Technical & Troubleshooting
  {
    id: 'not-therapy',
    category: 'Technical & Troubleshooting',
    icon: Shield,
    question: 'Is TENSION a replacement for therapy?',
    answer: 'No. TENSION is a self-awareness and behavioral tracking tool, not therapy or medical advice. It helps you recognize patterns and make informed decisions, but it cannot replace professional support. If you\'re experiencing severe anxiety, depression, relationship abuse, or suicidal thoughts, please contact a licensed therapist or crisis hotline (988 Suicide & Crisis Lifeline). TENSION works best as a complement to therapy, not a substitute.'
  },
  {
    id: 'data-not-syncing',
    category: 'Technical & Troubleshooting',
    icon: Smartphone,
    question: 'My data isn\'t syncing between devices. What should I do?',
    answer: '1) Ensure you\'re signed in with the same account on both devices, 2) Check that Cloud Sync is enabled in Settings → Sync & Backup, 3) Verify you have an active internet connection, 4) Try clicking the "Sync Now" button in Settings, 5) If still not working, export data from one device and import on the other as a temporary workaround. Contact support@tension.app if the issue persists.'
  },
  {
    id: 'lost-data',
    category: 'Technical & Troubleshooting',
    icon: Download,
    question: 'I lost my data! Can you recover it?',
    answer: 'Data recovery depends on where it was stored: If you had Cloud Sync enabled, sign in on any device and your data will restore automatically. If data was local-only, it may be recoverable from browser cache (contact support for instructions). If you previously exported a backup, you can re-import it in Settings → Import Data. This is why we strongly recommend enabling Cloud Sync (paid plans) or exporting monthly backups (free plan).'
  },
];

export default function FAQ() {
  useSEO({
    title: 'Relationship App FAQ | TENSION',
    description:
      'Answers about relationship tracking, conflict forecasting, privacy, pricing, and communication coaching inside TENSION.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      ['relationship app FAQ', 'couples app FAQ', 'relationship tension index'],
    ),
    canonical: '/faq',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });
  useStructuredData(
    generateFAQStructuredData(
      faqs.map((item) => ({
        question: item.question,
        answer: item.answer,
      })),
    ),
  );

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    analytics.track('faq_page_viewed');
  }, []);

  const categories = ['all', ...Array.from(new Set(faqs.map((faq) => faq.category)))];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <StockHeroStrip
          stockKey="coupleKitchen"
          alt="Warm home moment — illustrative imagery"
        />
        <div className="space-y-8">
          {/* Header */}
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-light tracking-wide text-neutral-900">Frequently Asked Questions</h1>
            <p className="mx-auto max-w-2xl text-lg text-neutral-600">
              Get answers to common questions about TENSION, tension scoring, privacy, and more.
            </p>
          </div>

          {/* Search */}
          <Card className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                className={selectedCategory === category ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0' : ''}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? 'All Questions' : category}
              </Button>
            ))}
          </div>

          {/* FAQ Accordion */}
          {filteredFAQs.length === 0 ? (
            <Card className="p-12 text-center">
              <HelpCircle className="w-12 h-12 text-neutral-300 dark:text-neutral-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No results found</h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Try a different search term or browse all categories
              </p>
            </Card>
          ) : (
            <Card className="p-6">
              <Accordion type="single" collapsible className="space-y-2">
                {filteredFAQs.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id} className="border-b border-neutral-200 dark:border-neutral-800 last:border-0">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-start gap-3 text-left">
                        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900 shrink-0 mt-1">
                          <faq.icon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-violet-600 dark:text-violet-400 mb-1 leading-snug">
                            {faq.category}
                          </p>
                          <p className="font-medium text-neutral-900 dark:text-neutral-100 leading-snug">
                            {faq.question}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pl-14 pr-4 pb-4">
                      <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          )}

          {/* Still Need Help */}
          <Card className="p-8 text-center bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30">
            <HelpCircle className="w-12 h-12 text-violet-600 dark:text-violet-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">Still need help?</h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6 max-w-lg mx-auto">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => navigate('/support')} size="lg">
                Contact Support
              </Button>
              <Button onClick={() => navigate('/about')} variant="outline" size="lg">
                Learn More About TENSION
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
